/**
 * Functions to extract the individual field types from a query.
 *
 * getQueryFields is the main entry point and calls
 * getFieldsFromAST & flattenFields
 *
 * getFieldsFromAST - Function to parse the graphQL query AST and return
 * a nested object of all fields included
 *
 * flattenFields - Once we have the nested object of fields this
 * function will create a flattened list of each field together
 * with the associated field type which is * obtained from the
 * graphQL schema
 *
 * The returned data is then used for views & filters etc.
 *
 * Example:
 *
 * Original query:
 *
 * customers {
 *   edges {
 *     node {
 *       id
 *       reference
 *       account {
 *         name
 *         address1
 *         address2
 *         address3
 *         address4
 *         address5
 *         postcode
 *         email
 *         telephoneNumber
 *         faxNumber
 *         country {
 *           description
 *         }
 *       }
 *       paymentTerm {
 *         description
 *       }
 *     }
 *   }
 *
 *   Returns:
 *
 *   {
 *    id: { type: "ID", name: "id" }
 *    reference: { type: "String", name: "reference" }
 *    account.address1: { type: "String", name: "account.address1" }
 *    account.country.description:
 *         { type: "String", name: "account.country.description" }
 *    ...
 *   }
 */
import schema from '../schema/schema.json';
import validationJson from '../schema/validation.json';
import fieldMetaData, { IColumnMetaData, IEnumMeta, IEnumMetaData, IWorkspaceExclusionMetaData } from './fieldMetaData';
import columnTitle from './columnTitle';
import { DefinitionNode, DocumentNode, FieldNode, SelectionNode, VariableNode } from 'graphql';
import { Maybe } from './p4types';
import { ExecutableDefinitionNode, FragmentDefinitionNode, FragmentSpreadNode } from 'graphql/language/ast';

const INCLUDE_VALIDATION = true;

function consoleWarn(...error: any) {
  // eslint-disable-next-line no-process-env
  if (process.env.NODE_ENV !== 'production') {
    console.warn(...error);
  }
}

type IValidation = {
  name: string;
  type: string;
  isEntity: boolean;
  pattern: string | null;
  mandatory: boolean;
  minValue: string | null;
  maxValue: string | null;
  isTransient: boolean;
  readOnly: boolean;
  isCalculated: boolean;
  typeParameters: Array<any>;
};

type IValidationSchema = {
  [entityType: string]: {
    [fieldName: string]: IValidation;
  };
};

export type IObjectField = {
  fieldName: string;
  type?: string;
  name?: string;
  title?: string;
  objectType?: string;
  validationRules: Partial<IValidation>;
  enumValues?: IEnumMeta<string>[] | null;
};

type INestedField = {
  [key: string]: INestedField | boolean;
};

type IField = {
  [key: string]: {
    [key: string]: INestedField;
  };
};

const validation = validationJson as IValidationSchema;

/*
 Create a nested object of fields from the provided GraphQL query AST
 */
function getFieldsFromAST(
  asts: SelectionNode | ReadonlyArray<SelectionNode>, //FragmentSpreadNode potentially also valid. Can't use SelectionNode due to an incompatible child.
  fragments?: ReadonlyArray<DefinitionNode>,
  init?: IField | INestedField,
  embeddedView = false,
  excludeFields: Maybe<Array<string>> = []
) {
  const initValue = init || {};
  const queryFragments = fragments || [];
  const queryAsts: Array<SelectionNode> = Array.isArray(asts) ? asts : [asts];

  return queryAsts.reduce((field: INestedField, val: SelectionNode) => {
    const { kind } = val;
    const name = (val as FieldNode | FragmentSpreadNode).name && (val as FieldNode | FragmentSpreadNode).name.value;
    if (kind === 'Field') {
      let skip = false;
      if (name === 'uuid') skip = true;
      if (val.directives && val.directives.length > 0) {
        if (excludeFields) {
          //Specified Exclusions (e.g. 'excludeCustomer')
          if (excludeFields.includes((val.directives[0].arguments![0].value as VariableNode).name.value)) skip = true;
        } else if (embeddedView) {
          if ((val.directives[0].arguments![0].value as VariableNode).name.value === 'embeddedView') skip = true;
        }
      }
      //If excludeFields are set, ignore skip & work from exclusions instead.
      if (!skip) {
        if (val.selectionSet) {
          field[name] = field[name] || ({} as INestedField);
          getFieldsFromAST(
            val.selectionSet.selections,
            queryFragments,
            field[name] as INestedField,
            embeddedView,
            excludeFields
          );
        } else if (!field || typeof (field as unknown) === 'boolean') {
          //Added console error instead of letting UI crash.
          //Actual issue leading to adding this was a missing subselection in a fragment,
          // but obscurely threw an error here as a result making it difficult to debug.
          console.error('Query inspection failure', { field, val }, 'Possibly cause: Missing subselection in fragment');
        } else {
          field[name] = true;
        }
      }
    } else if (kind === 'FragmentSpread') {
      const fragmentFields = getFragmentFieldsFromAST(
        fragments as ReadonlyArray<FragmentDefinitionNode>,
        val.name.value
      );

      for (const key in fragmentFields) {
        field[key] = fragmentFields[key];
      }
    }
    return field;
  }, initValue);
}

function isValidObject(value: any) {
  if (!value) {
    return false;
  }

  const isArray = Array.isArray(value);
  const isObject = Object.prototype.toString.call(value) === '[object Object]';
  const hasKeys = !!Object.keys(value).length;

  return !isArray && isObject && hasKeys;
}

/*
 Return the field information from the GraphQL schema
 */
function getFieldFromSchema(entityType: string, key: string) {
  if ((schema as any).types[entityType]) {
    return (schema as any).types[entityType].fields[key];
  }

  consoleWarn(
    `getFieldFromSchema: Entity ${entityType} with field ${key} missing from schema, try updating the schema or check entity is defined correctly`
  );
  return null;
}

function getMutationFieldFromSchema(entityType: string) {
  if ((schema as any).types[entityType]) {
    return (schema as any).types[entityType].inputFields;
  }
  consoleWarn(
    `getMutationFieldFromSchema: Entity ${entityType} missing from schema - try updating the schema or check entity is defined correctly`
  );
  return null;
}

/*
 Return the field validation rules from the validation json
 */
function getFieldValidationRules(entityType: string, key: string): IValidation | null {
  if (!validation[entityType]) {
    return null;
  }
  return validation[entityType][key];
}

/*
 Return the field metadata
 */
function getFieldMetaData(entityType: string, key: string) {
  let metaData = {};
  if (fieldMetaData[entityType] && (fieldMetaData[entityType] as IColumnMetaData)[key]) {
    metaData = (fieldMetaData[entityType] as IColumnMetaData)[key];
  }
  return metaData;
}

function getFieldName(entityType: string, field: string, fieldId: string, path: Array<string>) {
  let fieldName, fieldPrefix, parentName;
  if (columnTitle[entityType] && columnTitle[entityType][field]) {
    fieldName = columnTitle[entityType][field];
  } else {
    fieldName = fieldId.replace(/([A-Z])/g, ' $1');
    fieldName = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
  }
  if (Array.isArray(path) && path.length > 0) {
    parentName = path[path.length - 1];
    if (parentName !== 'headerValues') {
      fieldPrefix = parentName.replace(/([A-Z])/g, ' $1');
      fieldPrefix = fieldPrefix.charAt(0).toUpperCase() + fieldPrefix.slice(1);
      fieldName = `${fieldPrefix} - ${fieldName}`;
    }
  }
  return fieldName;
}

function createObjectField(
  path: Array<string>,
  field: string,
  entityType: string,
  childType: string,
  includeValidation: boolean
): { [key: string]: IObjectField } {
  let fieldId = path.concat([field]).join('.');
  fieldId = entityType ? fieldId.charAt(0).toLowerCase() + fieldId.slice(1) : fieldId.toLowerCase();
  const fieldTitle = getFieldName(entityType, fieldId, field, path);
  let validationRules: IValidation | null = {} as IValidation;
  let metaData = {};
  if (includeValidation) {
    validationRules = getFieldValidationRules(entityType, field);
    metaData = getFieldMetaData(entityType, field);
  }
  return {
    [fieldId]: {
      fieldName: fieldId,
      type: 'Object',
      name: fieldTitle,
      title: fieldTitle,
      objectType: childType,
      validationRules: {
        ...validationRules,
        ...metaData,
      },
    },
  };
}

/*
 Return the field type info
 */
function getFieldType(field: any) {
  let type = 'String';
  let { kind } = field;
  let { name } = field;

  if (kind === 'LIST' && field.ofType) {
    kind = field.ofType.kind;
    name = field.ofType.name;
  }

  switch (kind) {
    case 'SCALAR':
    case 'OBJECT':
    case 'ENUM':
      type = name;
      break;
    case 'NON_NULL': {
      type = field.ofType.name;
      break;
    }
  }
  return type;
}

function getEnumValues(entityType: string) {
  const enumValues: Array<IEnumMeta<string>> = [];
  if (fieldMetaData[entityType]) {
    (fieldMetaData[entityType] as IEnumMetaData).enumValues.forEach((option) => {
      enumValues.push({
        value: option.value,
        label: option.label,
      });
    });
  }
  return enumValues;
}

//Incomplete Enum
enum SchemaKind {
  Enum = 'ENUM',
  List = 'LIST',
}

/*
 Return the field object containing the field type & name
 The field type is obtained from the GraphQL schema by passing the
 entity type & field name
 */
function createField(
  path: Array<string>,
  fieldId: string,
  entityType: string,
  includeValidation: boolean
): { [key: string]: IObjectField } {
  const fieldName = path.concat([fieldId]).join('.');
  const field: {
    fieldName: string;
    type?: { kind?: SchemaKind; ofType?: { kind?: SchemaKind } }; //Note - Incomplete.
    name?: string;
    title?: string;
    validationRules?: any;
  } = getFieldFromSchema(entityType, fieldId);
  if (!field) {
    return {
      [fieldName]: {
        fieldName,
        type: undefined,
        name: undefined,
        title: undefined,
        validationRules: {},
      },
    };
  }

  const fieldType = getFieldType(field.type);

  const fieldTitle = getFieldName(entityType, fieldId, fieldId, path);
  let validationRules: IValidation | null = {} as IValidation;
  let metaData = {};
  let enumValues: Array<IEnumMeta<string>> | null = null;
  if (includeValidation) {
    validationRules = getFieldValidationRules(entityType, fieldId);
    metaData = getFieldMetaData(entityType, fieldId);
    const isRegularEnum = field?.type?.kind === SchemaKind.Enum;
    const isMultiSelectEnum = field?.type?.kind === SchemaKind.List && field.type.ofType?.kind === SchemaKind.Enum;
    enumValues = isRegularEnum || isMultiSelectEnum ? getEnumValues(fieldType) : null;
  }

  return {
    [fieldName]: {
      fieldName,
      type: fieldType,
      name: fieldTitle,
      title: fieldTitle,
      objectType: fieldType,
      validationRules: {
        ...validationRules,
        ...metaData,
      },
      enumValues,
    },
  };
}

/*
 Function to parse the nested field list and return a flattened list
 containing the field types
 */
function flattenFields(
  child: any,
  entityType: string,
  path: Array<string> = [],
  includeValidation = false,
  ignoreCollections = false
): { [key: string]: IObjectField } {
  const unFlattened: Array<{ [key: string]: IObjectField } | null> = Object.keys(child).map((fieldId: string) => {
    if (isValidObject(child[fieldId])) {
      const field = getFieldFromSchema(entityType, fieldId);

      if (!field) {
        consoleWarn(
          `Field missing from schema: ${fieldId} - try updating the schema or check field id provided in query`
        );
        return null;
      }
      if (ignoreCollections && field.type && field.type.kind === 'LIST') {
        return null; //Ignore Lists - Used for determining group by fields on widgets.
      }
      const childType: string = field.type.ofType ? field.type.ofType.name : field.type.name;
      if (fieldMetaData[childType] && (fieldMetaData[childType] as IWorkspaceExclusionMetaData).excludeFromWorkspace) {
        return null;
      }
      const nObjectField = createObjectField(path, fieldId, entityType, childType, includeValidation);
      const fField = flattenFields(
        child[fieldId],
        childType,
        path.concat([fieldId]),
        includeValidation,
        ignoreCollections
      );

      return Object.assign({}, nObjectField, fField);
    }
    return createField(path, fieldId, entityType, includeValidation);
  });

  return Object.assign({}, ...unFlattened);
}

export function getPaginatedQueryFields(
  query: Maybe<DocumentNode>,
  entityType: string,
  embeddedView?: boolean,
  excludeFields?: Array<string>,
  ignoreCollections?: boolean,
  { includeValidation = false } = {}
) {
  if (!query) {
    console.error('Undefined Query, Cannot Retrieve Fields for entity', entityType);
    return [];
  }

  const fields = getFieldsFromAST(
    (query.definitions[0] as ExecutableDefinitionNode).selectionSet.selections,
    query.definitions,
    {},
    embeddedView,
    excludeFields
  );

  let flatFields: { [key: string]: IObjectField };
  if ((fields[Object.keys(fields)[0]] as INestedField).edges) {
    flatFields = Object.assign(
      {},
      flattenFields(
        ((fields[Object.keys(fields)[0]] as INestedField).edges as INestedField).node,
        entityType,
        [],
        includeValidation,
        ignoreCollections
      )
    );
  } else {
    flatFields = Object.assign(
      {},
      flattenFields(fields[Object.keys(fields)[0]], entityType, [], includeValidation, ignoreCollections)
    );
  }

  return flatFields;
}

function getFragmentFieldsFromAST(fragments: ReadonlyArray<FragmentDefinitionNode>, fragmentName: string) {
  if (!fragments || !fragments.length) return {};
  for (let i = 0; i < fragments.length; i++) {
    const fragment = fragments[i];
    if (fragment.name.value === fragmentName) {
      return getFieldsFromAST(fragment.selectionSet.selections, fragments);
    }
  }
  return {};
}

const getDefinitionsAsTree = (
  selections: ReadonlyArray<SelectionNode>,
  name: string,
  definitions: ReadonlyArray<DefinitionNode>,
  path: Array<string> = []
) => {
  const fragments: Array<FragmentDefinitionNode> = [];
  const children: Array<{ name: string; fragments: DefinitionNode[]; children: any[]; path: string[] }> = [];

  selections.forEach((selection) => {
    if (selection.kind === 'FragmentSpread') {
      const definition = definitions.find(
        (potentialDefinition) => (potentialDefinition as ExecutableDefinitionNode).name!.value === selection.name.value
      );
      if (definition) {
        fragments.push(definition as FragmentDefinitionNode);
      }
    } else if (selection.selectionSet?.selections) {
      const nestedScan = getDefinitionsAsTree(
        selection.selectionSet.selections,
        (selection as FieldNode | FragmentSpreadNode).name.value,
        definitions,
        [...path, (selection as FieldNode | FragmentSpreadNode).name.value]
      );

      if (nestedScan.children.length > 0 || nestedScan.fragments.length > 0) {
        children.push(nestedScan);
      }
    }
  });

  return { name, fragments, children, path };
};

const processDefinition = (definition: DefinitionNode, type: string, { path = [] } = {}) => {
  //Get main fields
  const selectionFields = getFieldsFromAST((definition as ExecutableDefinitionNode).selectionSet.selections);
  return flattenFields(selectionFields, type, path, INCLUDE_VALIDATION);
};

export function getFragmentFields(query: DocumentNode) {
  if (!query) return {};

  let fields: Array<any> = [];

  const structureMap: Array<{
    definition: DefinitionNode;
    children: Array<any>;
    fragments: Array<FragmentDefinitionNode>;
  }> =
    query.definitions.length > 1
      ? query.definitions.map((definition) => {
          return {
            definition,
            ...getDefinitionsAsTree(
              (definition as ExecutableDefinitionNode).selectionSet.selections,
              (definition as ExecutableDefinitionNode).name!.value,
              query.definitions
            ),
          };
        })
      : [{ definition: query.definitions[0], children: [], fragments: [] }];

  (structureMap.length > 1
    ? structureMap.filter((structure) => structure.fragments.length > 0 || structure.children.length > 0)
    : structureMap
  ).forEach((structure) => {
    const definition = structure.definition as FragmentDefinitionNode;

    const type = definition.typeCondition?.name.value;
    fields = Object.assign(fields, processDefinition(definition, type));

    //Get fragment fields.
    structure.fragments.forEach((fragment) => {
      fields = Object.assign(fields, processDefinition(fragment, type));
    });

    //Get child fields.
    //Note - Only currently supporting 1 level deep, do we need to go further?
    structure.children.forEach((child) => {
      if (child.fragments?.length > 0) {
        const parent = validation[type];
        const childValidation = parent?.[child.path[0]];

        // Use typeParameters if available, fall back to type if it is not.
        const childType = childValidation?.typeParameters[0] ?? childValidation?.type ?? null;

        child.fragments.forEach((fragment: FragmentDefinitionNode) => {
          fields = Object.assign(fields, processDefinition(fragment, childType, { path: child.path }));
        });
      }
    });
  });

  return fields;
}

export function getMutationType(entityType: string) {
  const fields = getMutationFieldFromSchema(entityType);
  if (fields) {
    const inputType = Object.keys(fields);
    if (inputType.length > 2) {
      inputType.splice(0, 1);
      return inputType;
    }
    return inputType[1];
  }
  consoleWarn(`Migration type missing from schema: ${entityType} - try updating the schema`);
  return null;
}
